'use client';

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useBreakpoint } from 'hooks/useBreakpoints';
import * as Dialog from '@radix-ui/react-dialog';
import { RemoveCircle, AddCircle } from '@1debit/icons';
import { BaseCard, BaseCardBox } from 'components/shared/Card/BaseCard';
import { trackExpandableCardItemToggled } from 'components/analytics/eventHandlerTracking';
import { Colorways } from 'types/constants';
import { CardEyebrow } from './CardEyebrow';
import { CardHeadline } from './CardHeadline';
import { CardContent } from './CardContent';
import { INITIAL_RECT, getItemRect } from './utils';
import type { CardsExpandableItemProps, ItemRect } from './types';
import Media from '@/components/shared/Media/Media';

const TRANSITION_NORMAL = { duration: 0.3, ease: 'easeInOut' };
const TRANSITION_FAST = { duration: 0.1, ease: 'easeInOut' };

const CardsExpandableItemMobile = ({ item, parentId, position }: CardsExpandableItemProps) => {
  const { sys, eyebrow, cardCopy, cardHeadline, cardImage, cardListItems, aspectRatio } = item;
  const cardId = `card-${sys?.id}`;
  const headlineId = `headline-${parentId}-${position}`;
  const contentId = `content-${sys?.id}`;
  const { items: listItems } = cardListItems;
  const { isAboveSm } = useBreakpoint('sm');

  const [activeItemRect, setActiveItemRect] = useState<ItemRect | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const changeRect = useCallback((from: ItemRect | null, to: ItemRect | null, timeout = 100) => {
    setActiveItemRect(from);
    const timer = setTimeout(() => setActiveItemRect(to), timeout);
    return () => clearTimeout(timer);
  }, []);

  const handleToggle = useCallback(
    (isExpanding: boolean) => {
      if (isAboveSm || !sys?.id) return;

      const itemRect = getItemRect(sys.id);
      if (!itemRect) return;

      if (isExpanding) {
        changeRect(itemRect, INITIAL_RECT, 100);
        setIsExpanded(true);
        setIsDialogOpen(true);
        trackExpandableCardItemToggled({ action: 'open' });
      } else {
        changeRect(itemRect, itemRect, 100);
        setIsExpanded(false);
        setTimeout(() => setIsDialogOpen(false), 200);
        trackExpandableCardItemToggled({ action: 'close' });
      }
    },
    [isAboveSm, sys?.id, changeRect],
  );

  return (
    <>
      <BaseCard
        color={Colorways.Inverse}
        data-handle={`${sys?.id}-handle`}
        className="relative w-full min-w-none xs:pt-6 pb-12 xs:pb-24 xs:px-5">
        <BaseCardBox id={sys?.id} role="region" aria-label={`card${position}`} className="flex flex-col gap-6 flex-1">
          <CardEyebrow eyebrow={eyebrow} cardId={cardId} />
          <CardHeadline cardHeadline={cardHeadline} cardId={cardId} headlineId={headlineId} />

          {cardImage && <Media media={cardImage} className="m-auto" sizes="100vw" aspectRatio={aspectRatio} />}

          <button
            className="p-2 absolute bottom-5 right-4 xs:bottom-10 xs:right-9 rounded-full cursor-pointer"
            onClick={() => handleToggle(true)}
            aria-controls={contentId}
            aria-expanded={isExpanded}
            aria-label="Expand card content">
            <AddCircle className="w-[32px] h-[32px]" />
          </button>
        </BaseCardBox>
      </BaseCard>

      <AnimatePresence>
        {isDialogOpen && !isAboveSm ? (
          <Dialog.Root open={true}>
            <Dialog.Portal>
              <Dialog.Content id={`${sys?.id}-dialog`} aria-hidden={!isExpanded}>
                <Dialog.Title className="sr-only">{item.name}</Dialog.Title>
                <Dialog.Description className="sr-only" />
                <motion.div
                  className="fixed pointer-events-all z-1000 overflow-hidden w-full min-w-none"
                  initial={{ ...activeItemRect }}
                  animate={{
                    transition: TRANSITION_NORMAL,
                    ...INITIAL_RECT,
                    width: '100%',
                    height: '100%',
                    position: 'fixed',
                    overscrollBehavior: 'contain',
                    overflow: 'scroll',
                    borderRadius: '0px',
                  }}
                  exit={{ transition: TRANSITION_FAST, ...activeItemRect }}>
                  <BaseCard color={Colorways.Inverse} className="relative w-full min-w-none h-full rounded-none">
                    <div className="fixed overflow-y-auto w-full h-full pt-6 pb-10 relative">
                      <BaseCardBox id={sys?.id} role="region" aria-labelledby={cardId} className="flex flex-col gap-6">
                        <CardEyebrow eyebrow={eyebrow} cardId={cardId} />
                        <CardHeadline cardHeadline={cardHeadline} cardId={cardId} headlineId={headlineId} />

                        {cardImage && (
                          <Media media={cardImage} className="m-auto" sizes="100vw" aspectRatio={aspectRatio} />
                        )}

                        <AnimatePresence>
                          {isExpanded || isAboveSm ? (
                            <motion.div
                              className="w-full border-t border-(--keyline-color)"
                              initial={{ width: '0%', opacity: 0 }}
                              animate={{ width: '100%', opacity: 1, transition: TRANSITION_NORMAL }}
                              exit={{ width: '0%', opacity: 0, transition: TRANSITION_FAST }}
                            />
                          ) : null}
                        </AnimatePresence>

                        <AnimatePresence>
                          {isExpanded || isAboveSm ? (
                            <motion.div
                              id={contentId}
                              aria-hidden={!isExpanded}
                              className="flex flex-col gap-5"
                              initial={{ opacity: 0, translateY: '40px' }}
                              animate={{
                                opacity: 1,
                                translateY: '0px',
                                transition: TRANSITION_NORMAL,
                              }}
                              exit={{ opacity: 0, translateY: '0px', transition: TRANSITION_FAST }}>
                              <CardContent cardCopy={cardCopy} listItems={listItems} isContentHidden={!isExpanded} />
                            </motion.div>
                          ) : null}
                        </AnimatePresence>

                        <button
                          className="flex items-center p-2 fixed bottom-4 right-2 rounded-full cursor-pointer"
                          onClick={() => handleToggle(false)}
                          aria-controls={contentId}
                          aria-expanded={isExpanded}
                          aria-label="Collapse card content">
                          <RemoveCircle className="w-[32px] h-[32px]" />
                        </button>
                      </BaseCardBox>
                    </div>
                  </BaseCard>
                </motion.div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default CardsExpandableItemMobile;
